// ─────────────────────────────────────────────────────────────────────────────
//  lib/support/supportTicketService.js
//  Client-side service — POSTs a support ticket as multipart/form-data.
//  Routes through the Next.js proxy so the httpOnly authToken cookie is
//  read server-side and forwarded as a Bearer token to the backend.
//
//  POST /api/support/ticket  →  backend POST /support/ticket
//  Fields:  name, email, mobile, queryType, orderId, reason, description,
//           videoUrl, message, category, product
//  Files:   images[] — field name "images", max 5 files × 10 MB each
//           Accepts both images AND documents (PDF, DOC, XLSX, CSV, TXT)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {{
 *   name:         string,
 *   email:        string,
 *   mobile:       string,
 *   queryType:    string,
 *   orderId?:     string,
 *   reason?:      string,
 *   description?: string,
 *   videoUrl?:    string,
 *   message?:     string,
 *   category?:    string,
 *   product?:     string,
 *   address?:     string,   — pickup address, auto-extracted from the invoice PDF
 *   area?:        string,   — same address, structured: street/locality part
 *   city?:        string,
 *   state?:       string,
 *   pincode?:     string,
 *   attachments?: File[],   — images AND/OR documents (PDF, DOC, XLSX…)
 *   images?:      File[],   — legacy alias, merged with attachments
 * }} payload
 * @returns {Promise<{ success: boolean, data?: any, message?: string }>}
 */
export async function createSupportTicket(payload) {
    const {
        name, email, mobile, queryType,
        orderId, issueTypeId, reason, description,
        videoUrl, message, category, product, productId, address,
        area, city, state, pincode,
        attachments = [],
        images      = [],   // legacy — merged below
    } = payload;

    const form = new FormData();

    form.append('name',      name      || '');
    form.append('email',     email     || '');
    form.append('mobile',    mobile    || '');
    form.append('queryType', queryType || '');

    if (orderId?.trim())     form.append('orderId',     orderId.trim());
    if (issueTypeId?.trim()) form.append('issueTypeId', issueTypeId.trim());
    if (reason?.trim())      form.append('reason',      reason.trim());
    if (description?.trim()) form.append('description', description.trim());
    if (videoUrl?.trim())    form.append('videoUrl',    videoUrl.trim());
    if (message?.trim())     form.append('message',     message.trim());
    if (category?.trim())    form.append('category',    category.trim());
    if (product?.trim())     form.append('product',     product.trim());
    if (productId?.trim())   form.append('productId',   productId.trim());
    if (address?.trim())     form.append('address',     address.trim());
    if (area?.trim())        form.append('area',        area.trim());
    if (city?.trim())        form.append('city',        city.trim());
    if (state?.trim())       form.append('state',       state.trim());
    if (pincode?.trim())     form.append('pincode',     pincode.trim());

    // Merge legacy `images` array with new `attachments` array, deduplicate by reference,
    // cap at 5, and send all under the field name "images" (what the backend multer expects).
    const allFiles = [...attachments, ...images.filter(f => !attachments.includes(f))];
    allFiles
        .filter(f => f instanceof File)
        .slice(0, 5)
        .forEach(file => form.append('images', file));

    // No Content-Type header — browser sets multipart boundary automatically
    const res = await fetch('/api/support/ticket', {
        method: 'POST',
        body:   form,
    });

    let json;
    try { json = await res.json(); } catch { json = {}; }

    if (!res.ok) {
        return {
            success: false,
            message: json?.message || `Request failed (${res.status})`,
        };
    }

    return { success: true, data: json?.data ?? json, message: json?.message };
}
